package com.dotdrives.repository

import com.dotdrives.client.DriveClient
import com.dotdrives.model.DriveSnapshotRequest
import com.dotdrives.model.DriveBoardSnapshot
import com.dotdrives.model.ActivityEvent
import com.dotdrives.model.Drive
import com.dotdrives.model.DriveFile
import com.dotdrives.model.DriveFinancials
import com.dotdrives.model.DrivePhase
import com.dotdrives.model.DrivePhaseTask
import com.dotdrives.model.DrivePhaseTemplate
import com.dotdrives.model.DriveSummary
import com.dotdrives.model.DriveTaskItem
import com.dotdrives.model.NewDrive
import com.dotdrives.model.Target

interface DriveRepository {
    suspend fun snapshot(orgId: String, request: DriveSnapshotRequest): Result<DriveBoardSnapshot>

    suspend fun templates(orgId: String): Result<List<DrivePhaseTemplate>>

    suspend fun phases(orgId: String, templateId: String): Result<List<DrivePhase>>

    suspend fun phaseTasks(orgId: String, phaseId: String): Result<List<DrivePhaseTask>>

    suspend fun targets(orgId: String): Result<List<Target>>

    suspend fun listActive(
        orgId: String,
        templateId: String,
        query: String?,
        stewardUserId: String?,
        targetId: String?,
        tag: String?,
        giverType: String?,
    ): Result<List<DriveSummary>>

    suspend fun listCompleted(
        orgId: String,
        page: Int,
        pageSize: Int,
        templateId: String? = null,
    ): Result<PagedResult<DriveSummary>>

    suspend fun financials(orgId: String, driveIds: List<String>): Result<Map<String, DriveFinancials>>

    suspend fun get(orgId: String, driveId: String): Result<Drive>

    suspend fun create(orgId: String, drive: NewDrive): Result<Drive>

    suspend fun movePhase(
        orgId: String,
        driveId: String,
        fromPhaseId: String,
        fromPhaseName: String,
        toPhaseId: String,
        toPhaseName: String,
        outcome: String?,
    ): Result<Unit>

    suspend fun tasks(orgId: String, driveId: String): Result<List<DriveTaskItem>>

    suspend fun setTaskComplete(
        orgId: String,
        driveId: String,
        taskId: String,
        isComplete: Boolean,
    ): Result<DriveTaskItem>

    suspend fun files(orgId: String, driveId: String): Result<List<DriveFile>>

    suspend fun activity(orgId: String, driveId: String): Result<List<ActivityEvent>>
}

class SupabaseDriveRepository(
    private val client: DriveClient = DriveClient(),
) : DriveRepository {
    override suspend fun snapshot(orgId: String, request: DriveSnapshotRequest): Result<DriveBoardSnapshot> =
        runCatching { client.snapshot(orgId, request) }

    override suspend fun templates(orgId: String): Result<List<DrivePhaseTemplate>> =
        runCatching { client.templates(orgId) }

    override suspend fun phases(orgId: String, templateId: String): Result<List<DrivePhase>> =
        runCatching { client.phases(orgId, templateId) }

    override suspend fun phaseTasks(orgId: String, phaseId: String): Result<List<DrivePhaseTask>> =
        runCatching { client.phaseTasks(orgId, phaseId) }

    override suspend fun targets(orgId: String): Result<List<Target>> =
        runCatching { client.targets(orgId) }

    override suspend fun listActive(
        orgId: String,
        templateId: String,
        query: String?,
        stewardUserId: String?,
        targetId: String?,
        tag: String?,
        giverType: String?,
    ): Result<List<DriveSummary>> =
        runCatching {
            client.listActive(orgId, templateId, query, stewardUserId, targetId, tag, giverType)
        }

    override suspend fun listCompleted(
        orgId: String,
        page: Int,
        pageSize: Int,
        templateId: String?,
    ): Result<PagedResult<DriveSummary>> =
        runCatching {
            val result = client.listCompleted(orgId, page, pageSize, templateId)
            PagedResult(items = result.items, page = page, totalCount = result.totalCount)
        }

    override suspend fun financials(
        orgId: String,
        driveIds: List<String>,
    ): Result<Map<String, DriveFinancials>> =
        runCatching { client.financials(orgId, driveIds) }

    override suspend fun get(orgId: String, driveId: String): Result<Drive> =
        runCatching { client.get(orgId, driveId) }

    override suspend fun create(orgId: String, drive: NewDrive): Result<Drive> =
        runCatching { client.create(orgId, drive) }

    override suspend fun movePhase(
        orgId: String,
        driveId: String,
        fromPhaseId: String,
        fromPhaseName: String,
        toPhaseId: String,
        toPhaseName: String,
        outcome: String?,
    ): Result<Unit> =
        runCatching {
            client.movePhase(orgId, driveId, fromPhaseId, fromPhaseName, toPhaseId, toPhaseName, outcome)
        }

    override suspend fun tasks(orgId: String, driveId: String): Result<List<DriveTaskItem>> =
        runCatching { client.tasks(orgId, driveId) }

    override suspend fun setTaskComplete(
        orgId: String,
        driveId: String,
        taskId: String,
        isComplete: Boolean,
    ): Result<DriveTaskItem> =
        runCatching { client.setTaskComplete(orgId, driveId, taskId, isComplete) }

    override suspend fun files(orgId: String, driveId: String): Result<List<DriveFile>> =
        runCatching { client.files(orgId, driveId) }

    override suspend fun activity(orgId: String, driveId: String): Result<List<ActivityEvent>> =
        runCatching { client.activity(orgId, driveId) }
}
